import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { businessSectorsToApi } from 'store/modules/assistant/utils';
import Organization, {
  SubmitHandler,
} from 'components/Forms/Organization/Organization';
import api from 'api';
import { useFormatMessage } from 'components/IntlProvider';
import SettingsMenu from '../../components/Settings/SettingsMenu';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2rem 1rem;
  width: 100%;
`;

const { ROUTES, Redirect } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

interface OrganizationInfoProps extends RouteComponentProps {
  organization?: OrganizationType;
  isAdmin: boolean;
  setUserOrganization: (organization: OrganizationType) => void;
}

const OrganizationInfo = ({
  organization,
  location,
  isAdmin,
  setUserOrganization,
}: OrganizationInfoProps) => {
  const t = useFormatMessage();
  const getOrganizationData = (id: number) =>
    api.resources.getOrganization({ id }).then(({ data }) => {
      setUserOrganization(data);
    });

  useEffect(() => {
    if (organization && !organization.business_sectors) {
      getOrganizationData(organization.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!organization) {
    return <Redirect to={ROUTES.DASHBOARD} />;
  }

  const updateOrganization: SubmitHandler = (values, formHelpers) => {
    return api.resources
      .updateOrganization({
        id: organization.id,
        data: {
          ...values,
          business_sectors_ids: businessSectorsToApi(values.business_sectors),
          logo:
            typeof values.logo === 'string'
              ? values.logo.startsWith('https')
                ? null
                : values.logo
              : null,
        },
      })
      .then(async () => {
        if (values.logo !== '') {
          const formData = new FormData();
          formData.append('logo', values.logo);
          await api.resources.updateOrganizationLogo({
            logo: formData,
            id: organization.id,
          });
        }
      })
      .finally(() => {
        formHelpers.setSubmitting(false);
        getOrganizationData(organization.id);
      });
  };

  return (
    <Dashboard
      title={t('profile.title', 'Settings')}
      location={location}
      headerTitle={t('organization.info.title', 'Organization')}
      headerSubtitle={t(
        'organization.info.desc',
        'Here you can find organization details'
      )}
    >
      <Grid item xs={12}>
        <SettingsMenu />
      </Grid>
      <Container>
        {organization.business_sectors && (
          <Grid item xs={12} sm={12} md={10} lg={6}>
            <Organization
              initialValues={{
                ...organization,
                isAdmin,
              }}
              onSubmit={updateOrganization}
            />
          </Grid>
        )}
      </Container>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    isAdmin: selectors.authSelectors.isOrganizationAdmin(state),
  }),
  {
    setUserOrganization: actions.authActions.setUserOrg,
  }
)(OrganizationInfo);
